import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, useEffect } from 'react'
import { Link, type LoaderFunctionArgs } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { ViewTransitionLink } from '#app/components/view-transition-link.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta, buildCaseStudyJsonLd, buildBreadcrumbJsonLd } from '#app/utils/meta.ts'
import { useNavigationTheme } from '#app/utils/navigation-theme-context.tsx'
import { type Route } from './+types/_marketing.cases.$slug.ts'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const cases = await prisma.caseStudy.findMany({
			where: { published: true },
			select: { slug: true },
		})
		return cases.map((c) => ({
			route: `/cases/${c.slug}`,
			priority: 0.7,
		}))
	}),
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data?.caseStudy) {
		return [{ title: 'Case not found' }]
	}
	const title = data.caseStudy.title
	const description = data.caseStudy.excerpt || data.caseStudy.description || ''
	return [
		...buildMeta({
			title: `${title} - Case Study`,
			description,
			url: `/cases/${data.caseStudy.slug}`,
			image: data.caseStudy.imageUrl,
		}),
		buildCaseStudyJsonLd({
			title,
			description,
			url: `/cases/${data.caseStudy.slug}`,
			image: data.caseStudy.imageUrl,
			client: data.caseStudy.client,
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', url: '/' },
			{ name: 'Cases', url: '/cases' },
			{ name: title, url: `/cases/${data.caseStudy.slug}` },
		]),
	]
}

export async function loader({ params }: LoaderFunctionArgs) {
	invariantResponse(params.slug, 'Slug is required')

	const caseStudy = await prisma.caseStudy.findUnique({
		where: { slug: params.slug },
		include: {
			services: true,
			images: {
				orderBy: { order: 'asc' },
			},
			metrics: {
				orderBy: { order: 'asc' },
			},
			blocks: {
				orderBy: { order: 'asc' },
			},
		},
	})

	invariantResponse(caseStudy, 'Case study not found', { status: 404 })

	// Fetch services and case studies for dynamic blocks
	const [services, allCaseStudies] = await Promise.all([
		prisma.service.findMany({
			select: { id: true, name: true, slug: true, description: true },
			orderBy: { name: 'asc' },
		}),
		prisma.caseStudy.findMany({
			where: { published: true },
			select: {
				id: true,
				title: true,
				slug: true,
				client: true,
				imageUrl: true,
				heroVideoMode: true,
				heroVideoPlaybackId: true,
			},
			orderBy: [{ order: 'asc' }, { id: 'asc' }],
		}),
	])

	return {
		caseStudy,
		services,
		allCaseStudies: allCaseStudies.map((cs) => ({
			...cs,
			videoMode: (cs.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
			videoPlaybackId: cs.heroVideoPlaybackId ?? undefined,
		})),
	}
}

// Animation variants
const fadeIn = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 },
}

export default function CaseStudyPage({ loaderData }: Route.ComponentProps) {
	const { caseStudy, services, allCaseStudies } = loaderData
	const containerRef = useRef<HTMLDivElement>(null)
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ['start start', 'end start'],
	})

	const headerParallax = useTransform(scrollYProgress, [0, 1], [0, -150])
	const headerOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

	const { setNavigationTheme } = useNavigationTheme()

	const hasBlocks = caseStudy.blocks && caseStudy.blocks.length > 0

	// Set navigation theme based on hero background color
	// Only set from case study database values when NOT using blocks
	// When using blocks, BlockRenderer will handle the navigation theme
	useEffect(() => {
		// Skip if we have blocks - BlockRenderer will handle navigation theme
		if (hasBlocks) return

		// Use the isDarkHero flag from the database, or default to true for dark brown
		const isDark = caseStudy.isDarkHero ?? true
		const heroColor = caseStudy.heroColor || '#2B2625'
		setNavigationTheme(isDark, heroColor)

		// Clean up when leaving the page (only if we set it)
		return () => {
			setNavigationTheme(false, 'transparent')
		}
	}, [caseStudy.isDarkHero, caseStudy.heroColor, hasBlocks, setNavigationTheme])

	return (
		<motion.main
			ref={containerRef}
			className="bg-white"
			initial="initial"
			animate="animate"
			exit="exit"
		>
			{/* Render blocks from the block editor if available - hero should be first block */}
			{hasBlocks ? (
				<BlockRenderer
					blocks={caseStudy.blocks}
					services={services}
					caseStudies={allCaseStudies}
				/>
			) : (
				/* Fallback Hero Section - only shown when NO blocks are configured */
				<motion.section
					className="relative overflow-hidden pt-32"
					style={{
						y: headerParallax,
						backgroundColor: caseStudy.heroColor || '#2B2625',
					}}
				>
					<motion.div
						className="absolute inset-0 bg-gradient-to-b from-transparent to-[#2B2625]/30"
						style={{ opacity: headerOpacity }}
					/>

					{/* Breadcrumb Navigation - Inside hero with transparent background */}
					<motion.nav
						className="relative z-10 px-6 py-4 md:px-12 lg:px-24"
						variants={fadeIn}
					>
						<div className="flex items-center gap-2 text-sm text-white/60">
							<ViewTransitionLink
								to="/"
								className="transition-colors hover:text-white"
							>
								Home
							</ViewTransitionLink>
							<span>/</span>
							<ViewTransitionLink
								to="/cases"
								className="transition-colors hover:text-white"
							>
								Cases
							</ViewTransitionLink>
							<span>/</span>
							<span className="text-white">{caseStudy.title}</span>
						</div>
					</motion.nav>

					<div className="relative z-10 mx-auto max-w-[1600px] px-8 py-16 md:px-16 md:py-20 lg:px-24 lg:py-28 xl:px-32 xl:py-32">
						{/* Main Title */}
						<motion.h1
							className="mb-8 text-[48px] leading-[1.2] font-black tracking-[-0.005em] text-[#f7f1e2] md:mb-6 md:text-[80px] lg:text-[110px] xl:text-[140px]"
							variants={fadeIn}
							style={{
								fontFamily:
									'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
								fontStretch: 'condensed',
								fontWeight: 900,
							}}
						>
							{caseStudy.title}
						</motion.h1>

						{/* Description Text */}
						{(caseStudy.excerpt || caseStudy.description) && (
							<motion.div className="max-w-5xl space-y-6" variants={fadeIn}>
								{caseStudy.excerpt && (
									<h2
										className="text-[20px] leading-[1.3] text-[#f7f1e2]/95 md:text-[26px] lg:text-[30px]"
										style={{
											fontFamily:
												'"Helvetica Neue", Helvetica, Arial, sans-serif',
											fontWeight: 500,
										}}
									>
										{caseStudy.excerpt}
									</h2>
								)}

								{caseStudy.description && (
									<p
										className="text-[16px] leading-[1.4] text-[#f7f1e2]/85 md:text-[20px] lg:text-[22px]"
										style={{
											fontFamily:
												'"Helvetica Neue", Helvetica, Arial, sans-serif',
											fontWeight: 400,
										}}
									>
										{caseStudy.description}
									</p>
								)}
							</motion.div>
						)}

						{/* Bottom Meta Information */}
						<motion.div
							className="mt-16 flex flex-wrap items-center gap-6 md:gap-8"
							variants={fadeIn}
						>
							{/* Client Name - Hidden on mobile, shown on desktop */}
							<div
								className="hidden text-[16px] text-[#f7f1e2]/90 md:block"
								style={{
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
									fontWeight: 600,
								}}
							>
								{caseStudy.client}
							</div>

							{/* Services Tags - clickable with hover effect */}
							{caseStudy.services.length > 0 && (
								<div
									className="flex items-center gap-4 text-[14px] md:gap-6 md:text-[15px]"
									style={{
										fontFamily:
											'"Helvetica Neue", Helvetica, Arial, sans-serif',
										fontWeight: 400,
									}}
								>
									{caseStudy.services.map((service) => (
										<Link
											key={service.id}
											to={`/diensten/${service.slug}`}
											className="text-[#f7f1e2]/70 underline-offset-2 transition-colors duration-200 hover:text-[#f7f1e2]/90 hover:underline"
										>
											{service.name}
										</Link>
									))}
								</div>
							)}
						</motion.div>
					</div>
				</motion.section>
			)}

			{/* Fallback: Case Content - Rendered from Markdown if available and no blocks */}
			{!hasBlocks && caseStudy.content && (
				<motion.section
					className="px-6 py-16 md:px-12 md:py-24 lg:px-24"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
				>
					<div className="mx-auto max-w-4xl">
						<div
							className="prose prose-lg prose-gray max-w-none"
							dangerouslySetInnerHTML={{ __html: caseStudy.content }}
						/>
					</div>
				</motion.section>
			)}

		</motion.main>
	)
}
